/**
 * Responsible for managing the chat window UI interactions
 * @module chatWindow
 */

import $ from 'jquery';
import registerJQueryPlugin from 'styleguide/lib/register-jquery-plugin';
import mergeOptions from 'styleguide/lib/merge-options';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import Keyboard from 'styleguide/modules/keyboard'; // Import the Keyboard module for trap focus

export const PLUGIN_NAME = 'chatWindow';
export const DEFAULTS = {
  chatButton: '.js-chat-window',
  aiSuggestedQuestionClass: 'js-bedrock-question',
  chatWindowTarget: '.js-chat-window-target',
  chatWindowOverlay: '.ai-chat-window-overlay',
  chatWindowContent: '.ai-chat-window__content',
  chatWindowClose: '.js-chat-window-close',
  askAiInput: '#chat-window-input',
  askAiButton: '#js-chat-window-ask-ai',
  feedbackButton: '.js-switch-text-status',
  somethingElseButton: '.js-chat-window--something-else',
  feedbackForm: '.js-chat-feedback-form',
  conversationContainer: '#js-chat-window-conversation',
  sizeguideBtnContainer: '#js-sizeguide-target',
  feedbackContainer: '#js-switch-text-target',
  feedbackButtonContainer: '#js-feedback-button-container',
  feedbackMessageContainer: '#js-submit-feedback-container',
  pendingContainer: undefined, // JQuery object set in logic
  responseContainer: undefined, // JQuery object set in logic
  suggestionsContainer: '#js-chat-window-suggestions',
  chatIntro: '#js-chat-window-intro', // Leading copy shown for the initial "Something else" view
  windowHeader: '.ai-chat-window__header', // Chat window header selector for a11y focus
  loadingText: 'Thinking',
  activeClass: 'is-active',
  hideClass: 'u-hide',
  messageClass: 'message',
  progressClass: 'ai-chat-window__content-progress',
  bedrockSessionId: null, // Will be set when bedrock agent is called
  loadingIntervalId: null, // Will be set when animation is shown
  loadingMessage: null, // Will store reference to current loading message
  loadingDuration: 1500,
  loadingInterval: 500,
  maxDots: 3,
  // Function references for keyboard focus management - with safety checks
  trapFocusFunc: Keyboard && Keyboard.trapFocus ? Keyboard.trapFocus.bind(Keyboard) : null,
  releaseFocusFunc: Keyboard && Keyboard.releaseFocus ? Keyboard.releaseFocus.bind(Keyboard) : null
};

/**
 * Sets focus to the chat window header for accessibility and traps focus inside the window
 * @param {Object} opts - Options containing the window header selector
 */
function focusWindowHeader(opts) {
  // Find the window header
  const $header = $(opts.windowHeader);
  
  // If header exists, set focus to it
  if ($header.length) {
    // Set tabindex if it doesn't have one already
    if (!$header.attr('tabindex')) {
      $header.attr('tabindex', '-1');
    }
    
    // Small delay to ensure DOM is ready and transitions are complete
    setTimeout(() => {
      $header.focus();
      
      // Trap focus in the chat window using jQuery object
      if (opts.chatWindowTarget && opts.trapFocusFunc && typeof opts.trapFocusFunc === 'function') {
        const $chatWindow = $(opts.chatWindowTarget);
        if ($chatWindow.length) {
          opts.trapFocusFunc($chatWindow); // Pass jQuery object
        }
      }
    }, 100);
  }
}

/**
 * Opens the chat window and displays the user's question
 * @param {jQuery} $el - Chat window button element that was clicked
 * @param {Object} opts - Options passed along from initialization
 */
function openChatWindow($el, opts) {
  const buttonText = $el.text();
  $('.ai-chat-window__content-progress').addClass('ai-chat-window__content-progress--disabled');
  // Store the trigger element for focus return
  $(opts.chatWindowTarget).data('lastFocusedElement', $el[0]);

  // Hide & reset feedback element
  resetFeedbackAndSuggestions(opts);
  
  // Open/show the chat window
  $(opts.chatWindowTarget).addClass(opts.activeClass);
  $(opts.chatWindowOverlay).addClass(opts.activeClass);

  // Hide feedback element
  $(opts.feedbackContainer).addClass(opts.hideClass);

  // Hide suggestions based on what was asked previously
  $(`${opts.suggestionsContainer} ${opts.chatButton}[data-rank='${$el.data('rank')}']`).addClass(opts.hideClass);
  
  // Lock scrolling on the body using body-scroll-lock
  const targetElement = document.querySelector(opts.chatWindowTarget);
  const scrollableContent = document.querySelector(opts.chatWindowContent);

  if (targetElement && scrollableContent) {
    disableBodyScroll(scrollableContent, {  // Pass scrollableContent instead of targetElement
      reserveScrollBarGap: true,
    });
  }

  // Append question to window
  const $questionContainer = $(`<div class='ai-chat-window__content-question u-weight--bold'></div>`);
  $questionContainer.append(`<div class='${opts.messageClass}'><span>${buttonText}</span></div>`);
  $(opts.conversationContainer).append($questionContainer);

  // Append loading container
  opts.pendingContainer = $(`<div></div>`);
  $(opts.conversationContainer).append(opts.pendingContainer);

  let showSizeguide = false;
  const rank = $el.data('rank');
  const answerClass = 'answer_' + rank;
  const recommendationsClass = 'recom_' + rank;
  // Append response and suggestions if any
  opts.responseContainer = $(`<div class='${opts.hideClass}'></div>`);
  $(opts.conversationContainer).append(opts.responseContainer);
  if ($el.data('answer')) {
    let answerData = '';
    if ($('.' + answerClass).length > 0) {
      answerData = $('.' + answerClass).html();
    } else {
      answerData = $el.data('answer');
      if ($el.data('sizeguide') == true && $(opts.sizeguideBtnContainer).length) {
        answerData = answerData + '<br><br>' + $(opts.sizeguideBtnContainer).html();
        showSizeguide = true;
      }
    }
    if (answerData.length > 0) {
      opts.responseContainer.append(`<p class='${answerClass}'>${answerData}</p>`);
    }
  }

  if ($el.data('recommendationsUrl')) {
    if ($('.' + recommendationsClass).length > 0) {
      const $recommendationsContainer = $(`<div class='${recommendationsClass}'></div>`);
      opts.responseContainer.append($recommendationsContainer);
      $recommendationsContainer.html($('.' + recommendationsClass).first().html());
    } else {
      const $recommendationsContainer = $(`<div class='${recommendationsClass}'></div>`);
      opts.responseContainer.append($recommendationsContainer);
      showLoadingIndicator(opts);
      $.get($el.data('recommendationsUrl'), (data) => {
        $recommendationsContainer.html(data);
        stopLoadingAnimation(opts);
        scrollToQuestion();
        if (showSizeguide == true) {
          bindSizeGuideButton();
        }
      });
    }
  }

  const $feedbackButton = $(opts.feedbackButton);
  $feedbackButton.data('question', buttonText.trim());
  $feedbackButton.data('answer', $el.data('answer'));
  $feedbackButton.data('position', $el.data('rank'));
  
  // Focus on the window header for accessibility and trap focus
  focusWindowHeader(opts);

  if(!$el.data('recommendationsUrl')) {
    opts.responseContainer.removeClass(opts.hideClass);
    $(opts.suggestionsContainer).show();
    $(opts.feedbackContainer).show();
    scrollToQuestion();
    if (showSizeguide == true) {
      bindSizeGuideButton();
    }
  }

  function scrollToQuestion() {
    // Scroll to the question container
    const $scrollableContent = $(opts.chatWindowContent);
    if ($scrollableContent.length && $questionContainer.length) {     
      const scrollTop = $questionContainer.offset().top - $scrollableContent.offset().top + $scrollableContent.scrollTop();
      $scrollableContent.animate({
        scrollTop: scrollTop - 10
      }, 150);
    }
  }
}

function bindSizeGuideButton() {
  if (typeof window.bindSizeGuideEvent === 'function') {
    window.bindSizeGuideEvent();
  }
}

/**
 * Displays a loading indicator while waiting for response
 * @param {Object} opts - Options for configuring the loading indicator
 */
function showLoadingIndicator(opts) {
  // Disable all previous loading animations by adding disabled class
  $(`.${opts.progressClass}`).not('.ai-chat-window__content-progress--disabled')
    .addClass('ai-chat-window__content-progress--disabled');
  
  // Create a loading message element
  const $loadingMessage = $(`<div class='${opts.progressClass} u-padding-rl--xl'>${opts.loadingText}</div>`);
  opts.pendingContainer.append($loadingMessage);

  // Make loading message accessible to screen readers
  $loadingMessage.attr('aria-live', 'polite');
  
  // Store reference to loading message for external access
  opts.loadingMessage = $loadingMessage;
  
  // Clear any existing interval before starting a new one
  if (opts.loadingIntervalId) {
    clearInterval(opts.loadingIntervalId);
    opts.loadingIntervalId = null;
  }

  // Animate dots
  let dots = 0;
  opts.loadingIntervalId = setInterval(() => {
    // Check if disabled class exists - if so, pause the animation
    if ($loadingMessage.hasClass('ai-chat-window__content-progress--disabled')) {
      return; // Skip this iteration, but keep interval running
    }
    
    dots = (dots + 1) > opts.maxDots ? 1 : dots + 1;
    $loadingMessage.text(`${opts.loadingText}${'.'.repeat(dots)}`);
  }, opts.loadingInterval);
}

function stopLoadingAnimation(options, includeFeedbackContainer) {
  // Show response after timeout
  setTimeout(() => {
    clearInterval(options.loadingIntervalId);
    options.pendingContainer.remove();
    options.responseContainer.removeClass(options.hideClass);
    if (includeFeedbackContainer) {
      $(options.feedbackContainer).show();
    }
    options.pendingContainer = undefined;
    options.responseContainer = undefined;
  }, options.loadingDuration);
}

/**
 * Closes the chat window
 * @param {Object} opts - Options containing selectors and class names
 */
function closeChatWindow(opts) {
  const $chatWindow = $(opts.chatWindowTarget);
  
  // Hide the window
  $chatWindow.removeClass(opts.activeClass);
  $(opts.chatWindowOverlay).removeClass(opts.activeClass);
  
  // Release focus trap with proper jQuery object
  if (opts.releaseFocusFunc && typeof opts.releaseFocusFunc === 'function') {
    if ($chatWindow.length && $chatWindow) {
        opts.releaseFocusFunc($chatWindow); // Pass jQuery object
      }
    }
  
  // Remove body scroll lock
  clearAllBodyScrollLocks();
  
  // Clear any running intervals
  if (opts.loadingIntervalId) {
    clearInterval(opts.loadingIntervalId);
    opts.loadingIntervalId = null;
  }
  
  // Reset the containers
  $(opts.conversationContainer).empty();
  $(`${opts.suggestionsContainer} ${opts.chatButton}`).removeClass(opts.hideClass);
  
  // Return focus to the last clicked button if it exists and is stored
  const lastFocusedElement = $chatWindow.data('lastFocusedElement');
  if (lastFocusedElement) {
    // Check if element still exists in DOM
    if (document.contains(lastFocusedElement)) {
      lastFocusedElement.focus();
    }
  }
}

/**
 * Handles the "something else" button click
 * @param {jQuery} $el - Button element that was clicked
 * @param {Object} opts - Options passed along from initialization
 */
function handleSomethingElseButton($el, opts) {
  // Store the trigger element for focus return
  $(opts.chatWindowTarget).data('lastFocusedElement', $el[0]);

  // Hide & reset feedback element
  resetFeedbackAndSuggestions(opts);
  
  // Open/show the chat window
  $(opts.chatWindowTarget).addClass(opts.activeClass);
  $(opts.chatWindowOverlay).addClass(opts.activeClass);
  
  // Show the leading copy and suggestion bubbles for the initial custom-prompt view.
  $(opts.chatIntro).removeClass(opts.hideClass);
  $(opts.suggestionsContainer).show();

  // Lock scrolling on the body
  const targetElement = document.querySelector(opts.chatWindowTarget);
  if (targetElement) {
    disableBodyScroll(targetElement);
  }
  
  // Focus on the window header for accessibility and trap focus
  focusWindowHeader(opts);
}

function handleAskAiButton($trigger, options) {
  const $askAiInput = $(options.askAiInput);
  const question = $askAiInput.val().trim();
  if (!question) {
    return;
  }
  logEngagementWithDetails(window.location.href, 'PDP_GenQA', 'ask_question', question);

  // Store the trigger element for focus return when closing
  $(options.chatWindowTarget).data('lastFocusedElement', $trigger);

  // Hide & reset feedback element
  resetFeedbackAndSuggestions(options);

  // Append question to window
  const $questionContainer = $(`<p class='ai-chat-window__content-question u-weight--bold'></p>`);
  $questionContainer.append($(`<div class='${options.messageClass}'></div>`).text(question));
  $(options.conversationContainer).append($questionContainer);

  // Append loading container
  options.pendingContainer = $(`<div></div>`);
  $(options.conversationContainer).append(options.pendingContainer);

  askBedrock(options, question);

  // Reset input val
  $askAiInput.val('');

  // Focus on the window header for accessibility and trap focus
  focusWindowHeader(options);
}

function askBedrock(options, prompt) {
  const $askAiBtn = $(options.askAiButton);

  // Append response
  options.responseContainer = $(`<div class='${options.hideClass}'></div>`);
  $(options.conversationContainer).append(options.responseContainer);
  const $answerContainer = $(`<p class="answer_"></p>`);
  options.responseContainer.append($answerContainer);
  const sessionId = getSessionId(options);
  const bedrockChatUrl = `${$askAiBtn.data('url')}?prompt=${encodeURIComponent(prompt)}`
    + `&sessionId=${sessionId}&productCode=${window.rcProps.pdp.code}`;

  // Show loading animation
  showLoadingIndicator(options);

  fetch(bedrockChatUrl).then(response => {
    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");

    reader.read().then(function process({done, value}) {
      const str = decoder.decode(value);
      const data = JSON.parse(str);

      const $feedbackButton = $(options.feedbackButton);
      $feedbackButton.data('question', prompt);
      $feedbackButton.data('answer', $answerContainer.text().trim());
      $answerContainer.html(data.markup);

      if (Array.isArray(data.questions) && data.questions.length) {
        $(`${options.suggestionsContainer} ${options.chatButton}`).addClass(options.hideClass);
        const $suggestions = $(options.suggestionsContainer);

        data.questions.reverse().forEach(question => {
          const $suggestionButton = $('<button class="pill-button pill-button--light-grey u-margin-b--md '
            + `u-flex-no-wrap u-flex u-wrap u-left ${options.aiSuggestedQuestionClass}">`);
          $suggestionButton.text(`${question}`);
          $suggestions.prepend($suggestionButton);
        });
      }

      options.loadingDuration = 0;
      stopLoadingAnimation(options, true);
    }).catch(error => {
      console.log(error);
      $answerContainer.text('Something went wrong, please try your question again. ' +
        'If the problem persists, please reach out to our customer service.')

      options.loadingDuration = 0;
      stopLoadingAnimation(options, false);
    });
  }).catch(error => {
    console.log(error);
    $answerContainer.text('Something went wrong, please try your question again. ' +
      'If the problem persists, please reach out to our customer service.')

    options.loadingDuration = 0;
    stopLoadingAnimation(options, false);
  });
}

function handleFeedbackButton($trigger, options) {
  // 1. Cache selectors and initial data
  const $feedbackContainer = $(options.feedbackContainer);
  const $feedbackButton = $(options.feedbackButton);
  const $feedbackForm = $(options.feedbackForm);
  const $suggestionsContainer = $(options.suggestionsContainer);
  const isHelpful = $trigger.data('feedback') === 1;

  // 2. Helper: Reset UI State (Use classes instead of inline CSS if possible!)
  const resetUI = () => {
    $feedbackButton.prop('disabled', false).css({ background: "", borderRadius: "" });
    $trigger.prop('disabled', true).css({ background: '#e4e4e4', borderRadius: '50%' });
  };

  // 3. Helper: Final Transition (The "Thank You" sequence)
  const showSuccessState = () => {
    $feedbackContainer.fadeOut(500, function() {
      $trigger.prop('disabled', false);
      $(options.feedbackButtonContainer).addClass('u-hide');
      $(options.feedbackMessageContainer).removeClass('u-hide');
      // Reveal suggestions only once the transition has fully settled, so
      // they don't pop in mid-fade while the form/thumbs row are still moving.
      $feedbackContainer.fadeIn(500, function() {
        $suggestionsContainer.show();
      });
    });
  };

  // 4. Helper: Centralized Ajax Logic
  const sendFeedback = (submissionData) => {
    $.ajax({
      url: $feedbackContainer.data('url'),
      method: 'POST',
      contentType: 'application/json',
      data: JSON.stringify(submissionData),
      success: () => {
        $feedbackButton.prop('disabled', false).css({ background: "", borderRadius: "" });
        $feedbackButton.data('question', '').data('answer', '').data('position', -1);

        if (!isHelpful) {
          $feedbackForm.fadeOut(300, function() {
            $feedbackForm.trigger("reset").addClass('u-hide');
          });
        } else {
          $feedbackForm.trigger("reset");
        }

        showSuccessState();
      }
    });
  };

  // --- Execution Logic ---
  resetUI();

  // scroll to form similar
  function scrollToForm() {
    const $chatWindowContent = $(options.chatWindowContent);
    const $feedbackForm = $(options.feedbackForm);
    const scrollTop = $feedbackForm.offset().top - $chatWindowContent.offset().top + $chatWindowContent.scrollTop();
    $chatWindowContent.animate({
      scrollTop: scrollTop - 10
    }, 150);
  }

  const data = {
    code: $feedbackContainer.data('code'),
    question: $trigger.data('question'),
    answer: $trigger.data('answer'),
    position: $trigger.data('position'),
    feedback: isHelpful ? 'Helpful' : 'Not Helpful',
  };

  if (!isHelpful) {
    // Show form and wait for submission
    $feedbackForm.fadeIn().removeClass('u-hide');

    // Hide questions
    $suggestionsContainer.hide();

    // Scroll to form
    scrollToForm();

    // Use .off() to ensure we don't stack multiple listeners
    $(document).off("submit", options.feedbackForm).on("submit", options.feedbackForm, function(e) {
      e.preventDefault();
      const formData = $(this).serializeArray();
      
      // Add the form data to our existing object
      sendFeedback({ ...data, feedbackForm: formData });
    });
  } else {
    $suggestionsContainer.show();
    // It is helpful: Hide form and send immediately
    $feedbackForm.hide();
    sendFeedback(data);
  }
}

function getSessionId(options) {
  if (options.bedrockSessionId === null) {
    options.bedrockSessionId = crypto.randomUUID();
  }
  return options.bedrockSessionId;
}

function resetFeedbackAndSuggestions(options) {
  $(options.feedbackContainer).hide();
  $(options.feedbackButtonContainer).removeClass('u-hide');
  $(options.feedbackMessageContainer).addClass('u-hide');
  const $feedbackButton = $(options.feedbackButton);
  $feedbackButton.prop('disabled', false).css({ background: "", borderRadius: "" });
  $feedbackButton.data('question', '');
  $feedbackButton.data('answer', '');
  $feedbackButton.data('position', -1);
  $(`.${options.aiSuggestedQuestionClass}`).remove();
  // Hide the initial-view leading copy by default; it's re-shown only for that flow.
  $(options.chatIntro).addClass(options.hideClass);
}

/**
 * Applies event handlers for chat window interactions
 * @param {jQuery} $el - Container element for the chat window
 * @param {Object} opts - Options used for configuring the chat window
 */
function applyEventHandlers($el, opts) {
  // Remove any previously bound events to prevent duplicates
  $(opts.chatButton).off('click.chatWindow');
  $(document).off('click.chatWindow', `.${opts.aiSuggestedQuestionClass}`);
  $(opts.chatWindowClose).off('click.chatWindow');
  $(opts.chatWindowOverlay).off('click.chatWindow');
  $(opts.askAiButton).off('click.chatWindow');
  $(opts.feedbackButton).off('click.chatWindow');
  $(opts.somethingElseButton).off('click.chatWindow');
  $(opts.askAiInput).off('keydown.chatWindow');
  $(document).off('keydown.chatWindow');
  
  // Regular chat window buttons (excluding "something else" buttons)
  $(opts.chatButton).not(opts.somethingElseButton).on('click.chatWindow', function() {
    const buttonText = $(this).text();
    openChatWindow($(this), opts);
    logEngagementWithDetails(window.location.href, 'PDP_GenQA', 'click', buttonText.trim());
  });

  $(document).on('click.chatWindow', `.${opts.aiSuggestedQuestionClass}`, function() {
    const buttonText = $(this).text();
    resetFeedbackAndSuggestions(opts);
    $(`${opts.suggestionsContainer} ${opts.chatButton}`).addClass(opts.hideClass);

    // Append question to window
    const $questionContainer = $(`<p class='ai-chat-window__content-question'></p>`);
    $questionContainer.append(`<div class='${opts.messageClass}'>${buttonText}</div>`);
    $(opts.conversationContainer).append($questionContainer);

    // Append loading container
    opts.pendingContainer = $(`<div></div>`);
    $(opts.conversationContainer).append(opts.pendingContainer);

    askBedrock(opts, buttonText);

    // Focus on the window header for accessibility and trap focus
    focusWindowHeader(opts);

    logEngagementWithDetails(window.location.href, 'PDP_GenQA', 'click_bedrock', buttonText.trim());
  });

  // "Ask" button handling
  $(opts.askAiButton).on('click.chatWindow', function() {
    handleAskAiButton($(this), opts);
  });

  // Feedback button handling
  $(opts.feedbackButton).on('click.chatWindow', function() {
    handleFeedbackButton($(this), opts);
  });
  
  // "Something else" button handling
  $(opts.somethingElseButton).on('click.chatWindow', function() {
    handleSomethingElseButton($(this), opts);
    logEngagementWithDetails(window.location.href, 'PDP_GenQA', 'ask_question', '');
  });

  // Chat window close button
  $(opts.chatWindowClose).on('click.chatWindow', function() {
    closeChatWindow(opts);
  });

  // Click overlay to close - ADD THIS
  $(opts.chatWindowOverlay).on('click.chatWindow', function() {
    closeChatWindow(opts);
  });
  // Input field keydown handling - Submit on Enter
  $(opts.askAiInput).on('keydown.chatWindow', function(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAskAiButton($(this), opts);
    }
  });
  
  // Handle escape key to close the chat window
  $(document).on('keydown.chatWindow', function(e) {
    if (e.key === 'Escape' && $(opts.chatWindowTarget).hasClass(opts.activeClass)) {
      closeChatWindow(opts);
    }
  });

  // Handle mostly clearing bedrock agent session if any
  $(window).on('beforeunload', function() {
    if ($(opts.chatWindowTarget).hasClass(opts.activeClass)) {
      closeChatWindow(opts);
    }
  });
}

/**
 * Initializes the chat window functionality
 * @param {HTMLElement} el - Container element for the chat window
 * @param {Object} options - Options used for configuring the chat window
 * @see DEFAULTS
 */
export default function chatWindow(el, options = {}) {
  const $el = $(el);
  
  // Check if already initialized to prevent multiple bindings
  if ($el.data('chatWindowInitialized')) {
    return;
  }
  
  const opts = mergeOptions(DEFAULTS, options, $el);
  
  // Initialize a11y attributes once
  initializeA11yAttributes(opts);
  
  applyEventHandlers($el, opts);
  
  // Mark as initialized
  $el.data('chatWindowInitialized', true);
}

/**
 * Sets up accessibility attributes on the chat window elements
 * @param {Object} opts - Options containing selectors
 */
function initializeA11yAttributes(opts) {
  // Set up header for accessibility
  const $header = $(opts.windowHeader);
  if ($header.length) {
    // Ensure the header can receive focus
    if (!$header.attr('tabindex')) {
      $header.attr('tabindex', '-1');
    }
    
    // Add appropriate ARIA role and label if not present
    if (!$header.attr('role')) {
      $header.attr('role', 'dialog');
    }
    
    if (!$header.attr('aria-labelledby') && $header.find('h2, h3, h4').length) {
      const headingId = 'chat-window-heading';
      $header.find('h2, h3, h4').first().attr('id', headingId);
      $header.attr('aria-labelledby', headingId);
    } else if (!$header.attr('aria-label')) {
      $header.attr('aria-label', 'Chat Window');
    }
  }
  
  // Make close button accessible
  $(opts.chatWindowClose).attr('aria-label', 'Close chat window');
  
  // Set appropriate ARIA attributes on the chat window
  $(opts.chatWindowTarget).attr({
    'role': 'dialog',
    'aria-modal': 'true'
  });
}

registerJQueryPlugin(PLUGIN_NAME, chatWindow);



// WEBPACK FOOTER //
// ./src/scripts/entry-points/pdp/chat-window-qa.js